import { PHASE, REMAINING_PLAYOFF_TEAMS_PHASES } from "../../../common/constants.ts";
import { g, helpers } from "../util/index.ts";
import type { PlayerStatType } from "../../../common/types.ts";
import type { Params } from "../../../common/types/router.ts";
import { actualPhase } from "../util/actualPhase.ts";

/**
 * Validate that a given abbreviation corresponds to a team.
 *
 * If the abbreviation is not valid, then g.get("userTid") and its correspodning abbreviation will be returned.
 *
 * @memberOf util.helpers
 * @param  {string} abbrev Three-letter team abbreviation, like "ATL". Can also be a numeric team ID like "0" or a concatenated one like "ATL_0", in which case the number will be used.
 * @return {Array} Array with two elements, the team ID and the validated abbreviation.
 */
export const validateAbbrev = (abbrev?: string, strict?: boolean): [number, string] => {
  const teamInfoCache = g.get("teamInfoCache");

  if (abbrev !== undefined) {
    {
      const tid = teamInfoCache.findIndex((t) => t.abbrev === abbrev);
      if (tid >= 0) {
        return [tid, abbrev];
      }
    }

    {
      const parts = abbrev.split("_");
      const int = Number.parseInt(parts.at(-1)!);
      if (!Number.isNaN(int) && teamInfoCache[int]) {
        return [int, teamInfoCache[int].abbrev];
      }
    }
  }

  if (strict) {
    return [g.get("userTid"), "???"];
  }

  const tid = g.get("userTid");
  return [tid, teamInfoCache[tid]?.abbrev ?? "???"];
};

/**
 * Validate the given season.
 *
 * Currently this doesn't really do anything except replace "undefined" with g.get("season").
 *
 * @memberOf util.helpers
 * @param {number|string|undefined} season The year of the season to validate. If undefined, then g.get("season") is used.
 * @return {number} Validated season (same as input unless input is undefined, currently).
 */
export const validateSeason = (season: number | string | undefined): number => {
  if (season === undefined) {
    return g.get("season");
  }

  if (typeof season === "string") {
    season = Number.parseInt(season);
  }

  if (Number.isNaN(season)) {
    return g.get("season");
  }

  return season;
};

import type { SeasonType } from "../../../common/types/processInputs.ts";
export type { SeasonType };

const validateSeasonType = (
  seasonType: string | undefined,
  defaultType: SeasonType = "regularSeason",
): SeasonType => {
  if (seasonType === "playoffs") {
    return "playoffs";
  } else if (seasonType === "regularSeason") {
    return "regularSeason";
  } else if (seasonType === "combined") {
    return "combined";
  } else {
    return defaultType;
  }
};

const draft = () => {
  if (
    g.get("phase") !== PHASE.DRAFT &&
    g.get("phase") !== PHASE.AFTER_DRAFT &&
    g.get("phase") !== PHASE.FANTASY_DRAFT &&
    g.get("phase") !== PHASE.EXPANSION_DRAFT
  ) {
    return {
      redirectUrl: helpers.leagueUrl(["draft_scouting"]),
    };
  }

  if (g.get("phase") === PHASE.EXPANSION_DRAFT && g.get("expansionDraft").phase === "protection") {
    return {
      redirectUrl: helpers.leagueUrl(["protect_players"]),
    };
  }
};

const draftLottery = (params: Params) => {
  const season = validateSeason(params.season);
  return {
    season,
  };
};

const draftScouting = (params: Params) => {
  return {
    season: params.season !== undefined ? validateSeason(params.season) : undefined,
  };
};

const draftHistory = (params: Params) => {
  let season: number;

  const draftAlreadyHappened = g.get("phase") >= PHASE.DRAFT;
  const currentSeason = g.get("season");

  if (
    (params.season === undefined || params.season === String(currentSeason)) &&
    !draftAlreadyHappened
  ) {
    // View last season by default
    season = currentSeason - 1;
  } else {
    season = validateSeason(params.season);
  }

  if (season > currentSeason || (season === currentSeason && !draftAlreadyHappened)) {
    // Future draft class
    return {
      redirectUrl: helpers.leagueUrl(["draft_scouting"]),
    };
  }

  return {
    season,
  };
};

const freeAgents = (params: Params) => {
  if (g.get("phase") === PHASE.RESIGN_PLAYERS) {
    return {
      redirectUrl: helpers.leagueUrl(["negotiation"]),
    };
  }

  let season: number | "current";
  if (params.season && params.season !== "current") {
    season = validateSeason(params.season);
  } else {
    season = "current";
  }

  let type: "available" | "signed" | "both";
  if (params.type === "signed") {
    type = "signed";
  } else if (season !== "current") {
    // If this is a previous season, force type to be "both" because "available" will be none and "both" looks better when switching to current season than "signed"
    type = "both";
  } else if (params.type === "both") {
    type = "both";
  } else {
    type = "available";
  }

  return {
    season,
    type,
  };
};

const gameLog = (params: Params) => {
  const [tid, abbrev] =
    params.abbrev === "special" ? [-1, "special"] : validateAbbrev(params.abbrev);

  return {
    gid: params.gid !== undefined ? Number.parseInt(params.gid) : -1,
    season: validateSeason(params.season),
    tid,
    abbrev,
  };
};

const history = (params: Params) => {
  let season = validateSeason(params.season);

  // If playoffs aren't over, season awards haven't been set
  if (g.get("phase") >= 0 && g.get("phase") <= PHASE.PLAYOFFS) {
    // View last season by default
    if (season === g.get("season")) {
      season -= 1;
    }
  }

  return {
    season,
  };
};

const validateStatType = (statType: string | undefined): PlayerStatType => {
  if (statType === "perGame" || statType === "per_game") {
    return "perGame";
  } else if (statType === "per36" || statType === "per_36") {
    return "per36";
  } else if (statType === "totals") {
    return "totals";
  } else {
    return "perGame";
  }
};

const playerStatsStatTypes = [
  "perGame",
  "per36",
  "totals",
  "advanced",
  "shotLocations",
  "gameHighs",
] as const;

type PlayerStatsStatType = (typeof playerStatsStatTypes)[number];

const normalizePlayerStatsStatType = (statType: string | undefined): PlayerStatsStatType => {
  if (statType === "per_game") {
    return "perGame";
  }
  if (statType === "per_36") {
    return "per36";
  }
  if (statType === "shot_locations") {
    return "shotLocations";
  }
  if (statType === "game_highs") {
    return "gameHighs";
  }
  if (playerStatsStatTypes.includes(statType as PlayerStatsStatType)) {
    return statType as PlayerStatsStatType;
  }

  return "perGame";
};

const leaders = (params: Params) => {
  let season: "career" | "all" | number;
  if (params.season === "career" || params.season === "all") {
    season = params.season;
  } else {
    season = validateSeason(params.season);
  }

  return {
    season,
    playoffs: validateSeasonType(params.playoffs),
    statType: validateStatType(params.statType),
  };
};

const dailySchedule = (params: Params) => {
  // N3-C: the "整季视图" (full-season/team schedule) is merged into the
  // dailySchedule surface. A literal-"team" route carries the team abbrev and
  // selects the season view; the worker then also returns the season-schedule
  // bundle for that team.
  if (params.abbrev !== undefined) {
    const [tid, abbrev] = validateAbbrev(params.abbrev);
    return {
      cid: undefined,
      day: undefined,
      season: g.get("season"),
      seasonViewAbbrev: abbrev,
      seasonViewTid: tid,
    };
  }

  let cid;
  if (params.cid !== undefined && params.cid !== "all") {
    cid = Number.parseInt(params.cid);
    if (Number.isNaN(cid)) {
      cid = undefined;
    }
  }

  if (params.season === "today") {
    return {
      cid,
      day: undefined,
      season: g.get("season"),
      today: true,
    };
  }

  const season = validateSeason(params.season);

  let day = params.day === undefined ? undefined : Number.parseInt(params.day as any);
  if (Number.isNaN(day)) {
    day = 1;
  }

  return {
    cid,
    day,
    season,
  };
};

const liveGame = (params: Params, ctxBBGM: any) => {
  const obj: {
    fromAction: boolean;
    gid?: number;
    playByPlay?: any[];
  } = {
    fromAction: !!ctxBBGM.fromAction,
  };

  if (ctxBBGM.playByPlay !== undefined) {
    obj.gid = ctxBBGM.gidOneGame;
    obj.playByPlay = ctxBBGM.playByPlay;
  }

  return obj;
};

export const newLeague = (params: Params) => {
  let type: "random" | "real" = "random";
  if (params.x === "random") {
    type = "random";
  } else if (params.x === "real") {
    type = "real";
  }

  return {
    type,
  };
};

const news = (params: Params) => {
  const season = validateSeason(params.season);
  let level: "all" | "normal" | "big";
  if (params.level === "all") {
    level = "all";
  } else if (params.level === "normal") {
    level = "normal";
  } else {
    level = "big";
  }

  const order: "oldest" | "newest" = params.order === "oldest" ? "oldest" : "newest";

  let abbrev;
  let tid: number | undefined;
  const [validatedTid, validatedAbbrev] = validateAbbrev(params.abbrev, true);
  if (params.abbrev !== undefined && params.abbrev !== "all" && validatedAbbrev !== "???") {
    abbrev = validatedAbbrev;
    tid = validatedTid;
  } else {
    abbrev = "all";
  }

  return {
    abbrev,
    level,
    order,
    season,
    tid,
  };
};

const player = (params: Params) => {
  return {
    pid: params.pid !== undefined ? Number.parseInt(params.pid) : undefined,
  };
};

const playerRatings = (params: Params) => {
  let abbrev;
  let tid: number | undefined;

  const [validatedTid, validatedAbbrev] = validateAbbrev(params.abbrev, true);

  if (params.abbrev !== undefined && validatedAbbrev !== "???") {
    abbrev = validatedAbbrev;
    tid = validatedTid;
  } else if (params.abbrev === "watch") {
    abbrev = "watch";
  } else if (params.abbrev === "playoffs" && REMAINING_PLAYOFF_TEAMS_PHASES.has(actualPhase())) {
    abbrev = "playoffs";
  } else {
    abbrev = "all";
  }

  return {
    abbrev,
    season: validateSeason(params.season),
    tid,
  };
};

export const playerStats = (params: Params) => {
  let abbrev;

  const [, validatedAbbrev] = validateAbbrev(params.abbrev, true);

  if (params.abbrev !== undefined && validatedAbbrev !== "???") {
    abbrev = validatedAbbrev;
  } else if (params.abbrev === "watch") {
    abbrev = "watch";
  } else if (params.abbrev === "playoffs" && REMAINING_PLAYOFF_TEAMS_PHASES.has(actualPhase())) {
    abbrev = "playoffs";
  } else {
    abbrev = "all";
  }

  let season: "career" | "all" | number;
  if (params.season === "career" || params.season === "all") {
    season = params.season;
  } else {
    season = validateSeason(params.season);
  }

  return {
    abbrev,
    season,
    statType: normalizePlayerStatsStatType(params.statType),
    playoffs: validateSeasonType(params.playoffs),
  };
};

const roster = (params: Params) => {
  // Fix broken links
  if (params.abbrev === "FA" || params.abbrev === "FA_-1") {
    // https://stackoverflow.com/a/59923262/786644
    const returnValue = {
      redirectUrl: helpers.leagueUrl(["free_agents"]),
    };
    return returnValue;
  }

  const season = validateSeason(params.season);

  if (params.abbrev === "DP" || params.abbrev === "DP_-2") {
    let redirectUrl;
    const draftAlreadyHappened =
      season < g.get("season") || (season === g.get("season") && g.get("phase") > PHASE.DRAFT);
    if (draftAlreadyHappened) {
      redirectUrl = helpers.leagueUrl(["draft_history", season]);
    } else {
      redirectUrl = helpers.leagueUrl(["draft_scouting"]);
    }

    // https://stackoverflow.com/a/59923262/786644
    const returnValue = {
      redirectUrl,
    };
    return returnValue;
  }

  const [tid, abbrev] = validateAbbrev(params.abbrev);

  return { abbrev, playoffs: validateSeasonType(params.playoffs), season, tid };
};

const teamFinances = (params: Params) => {
  const show = params.show ?? "10";
  const [tid, abbrev] = validateAbbrev(params.abbrev);
  return { abbrev, show, tid };
};

const teamStats = (params: Params) => {
  const playoffs = params.playoffs === "playoffs" ? "playoffs" : "regularSeason";

  return {
    season: validateSeason(params.season),
    teamOpponent: params.teamOpponent ?? "team",
    playoffs,
  };
};

const standings = (params: Params) => {
  let type: "conf" | "league" = g.get("numGamesPlayoffSeries").length === 0 ? "league" : "conf";
  if (params.type === "league") {
    type = params.type;
  } else if (params.type === "conf" || params.type === "div") {
    // Division standings were removed. Preserve old bookmarks by resolving
    // their former route to the remaining conference standings.
    type = "conf";
  }

  return {
    season: validateSeason(params.season),
    type,
  };
};

const validateSeasonOnly = (params: Params) => {
  return {
    season: validateSeason(params.season),
  };
};

export default {
  dailySchedule,
  draft,
  draftLottery,
  draftScouting,
  draftHistory,
  freeAgents,
  gameLog,
  history,
  leaders,
  liveGame,
  newLeague,
  news,
  player,
  playerRatings,
  playerStats,
  playoffs: validateSeasonOnly,
  roster,
  standings,
  teamFinances,
  teamStats,
};
